import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../elements/Layout';
import { addCustomer } from '../../utils/api/api';
import Select from "react-select";
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Flag,
    CreditCard,
    Globe,
    Building,
    ChevronRight,
    Check,
    X,
    Loader2,
    Users,
    UserPlus,
    Image,
    Heart,
    UserCheck
} from 'lucide-react';

const fadeIn = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
};

const staggerContainer = {
    animate: {
        transition: {
            staggerChildren: 0.05
        }
    }
};

const FormSection = ({ title, icon, children }) => (
    <motion.div
        className="mb-6 bg-white rounded-2xl p-5 shadow-sm border border-gray-100"
        variants={fadeIn}
    >
        <div className="flex items-center mb-4 pb-3 border-b border-gray-100">
            <div className="w-8 h-8 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-600 mr-3">
                {icon}
            </div>
            <h3 className="text-gray-800 font-medium">{title}</h3>
        </div>
        <div className="space-y-4">
            {children}
        </div>
    </motion.div>
);

const FormRow = ({ children }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
    </div>
);

const FormField = ({ label, required = false, children, error }) => (
    <div className="relative">
        <label className="text-gray-700 text-sm font-medium mb-2 flex items-center">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
);

const AddNewCustomer = () => {
    const [countryCode, setCountryCode] = useState('');
    const [countryCodes, setCountryCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [photoPreview, setPhotoPreview] = useState(null);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        gender: '',
        phone_number: '',
        id_number: '',
        dob: '',
        country: '',
        nationality: '',
        city: '',
        state: '',
        customer_type: 'individual',
        participants: [],

        next_of_kin1_name: '',
        next_of_kin1_phone: '',
        next_of_kin1_email: '',
        next_of_kin1_idNo: '',
        next_of_kin1_relationship: '',

        next_of_kin2_name: '',
        next_of_kin2_phone: '',
        next_of_kin2_email: '',
        next_of_kin2_idNo: '',
        next_of_kin2_relationship: '',

        photo: null
    });

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const response = await fetch("https://restcountries.com/v3.1/all");
                const data = await response.json();
                const countryList = data
                    .sort((a, b) => a.name.common.localeCompare(b.name.common))
                    .map((country) => ({
                        name: country.name.common,
                        code: country.idd.root ? country.idd.root + (country.idd.suffixes ? country.idd.suffixes[0] : "") : "",
                        flag: country.flags.svg,
                    }));
                setCountryCodes(countryList);
            } catch (error) {
                console.error("Error fetching countries:", error);
                toast.error("Failed to load country data. Please enter country details manually.");
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries
    }, []);    

    const handleInputChange = (e) => {
        const { name, type, value, checked, files } = e.target;

        if (type === 'file' && files && files.length > 0) {
            const file = files[0];
            setFormData((prevData) => ({ ...prevData, photo: file }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);

        } else if (name === 'participants' && type === 'textarea') {
            const participantsList = value.split('\n').filter(item => item.trim() !== '');
            setFormData((prevData) => ({ ...prevData, participants: participantsList }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }

        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const handlePhoneNumber = (number) => {
        if (!number) return '';
        const cleanNumber = number.replace(/\D/g, '');
        const lastDigits = cleanNumber.slice(-9);
        return countryCode ? countryCode + lastDigits : lastDigits;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.first_name) newErrors.first_name = "First name is required";
        if (!formData.last_name) newErrors.last_name = "Last name is required";
        if (!formData.email) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
        if (!formData.phone_number) newErrors.phone_number = "Phone number is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!countryCode) newErrors.countryCode = "Country code is required";
        if (!formData.country) newErrors.country = "Country is required";
        if (!formData.id_number) newErrors.id_number = "ID number is required";
        if (!formData.dob) newErrors.dob = "Date of birth is required";
        if (!formData.nationality) newErrors.nationality = "Nationality is required";
        if (!formData.city) newErrors.city = "City is required";
        if (!formData.state) newErrors.state = "State is required";

        if (formData.customer_type === 'group' || formData.customer_type === 'joint') {
            if (!formData.participants || formData.participants.length === 0) {
                newErrors.participants = "Please add participants";
            }
        }

        if (!formData.next_of_kin1_name) newErrors.next_of_kin1_name = "Primary next of kin name is required";
        if (!formData.next_of_kin1_phone) newErrors.next_of_kin1_phone = "Primary next of kin phone is required";
        if (!formData.next_of_kin1_relationship) newErrors.next_of_kin1_relationship = "Relationship is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextStep = () => {
        const step1Errors = {};
        if (!formData.first_name) step1Errors.first_name = "First name is required";
        if (!formData.last_name) step1Errors.last_name = "Last name is required";
        if (!formData.email) step1Errors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) step1Errors.email = "Email is invalid";
        if (!formData.phone_number) step1Errors.phone_number = "Phone number is required";
        if (!formData.gender) step1Errors.gender = "Gender is required";
        if (!countryCode) step1Errors.countryCode = "Country code is required";
        if (!formData.id_number) step1Errors.id_number = "ID number is required";
        if (!formData.dob) step1Errors.dob = "Date of birth is required";

        if (Object.keys(step1Errors).length > 0) {
            setErrors(step1Errors);
            return;
        }

        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePrevStep = () => {
        setStep(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!validateForm()) return;

        const processedPhoneNumber = handlePhoneNumber(formData.phone_number);
        const processedNextOfKin1Phone = handlePhoneNumber(formData.next_of_kin1_phone);
        const processedNextOfKin2Phone = formData.next_of_kin2_phone ? handlePhoneNumber(formData.next_of_kin2_phone) : '';

        const processedParticipants = formData.participants.map(participant => ({
            ...participant,
            phone_number: handlePhoneNumber(participant.phone_number)
        }));

        const customerData = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            gender: formData.gender,
            phone_number: processedPhoneNumber,
            id_number: formData.id_number,
            dob: formData.dob,
            country: formData.country,
            nationality: formData.nationality,
            city: formData.city,
            state: formData.state,
            customer_type: formData.customer_type,

            next_of_kin1_name: formData.next_of_kin1_name,
            next_of_kin1_phone: processedNextOfKin1Phone,
            next_of_kin1_email: formData.next_of_kin1_email || '',
            next_of_kin1_idNo: formData.next_of_kin1_idNo || '',
            next_of_kin1_relationship: formData.next_of_kin1_relationship,

            next_of_kin2_name: formData.next_of_kin2_name || '',
            next_of_kin2_phone: processedNextOfKin2Phone,
            next_of_kin2_email: formData.next_of_kin2_email || '',
            next_of_kin2_idNo: formData.next_of_kin2_idNo || '',
            next_of_kin2_relationship: formData.next_of_kin2_relationship || '',

            ...(formData.customer_type === 'group' || formData.customer_type === 'joint'
                ? { participants: processedParticipants }
                : {}),
        };

        const headers = {
            'Content-Type': 'application/json'
        };

        setLoading(true);
        try {
            console.log('Submitting customer data:', customerData);
            await addCustomer(customerData, headers);
            toast.success("Customer added successfully");
            navigate('/dashboard/customers');
        } catch (error) {
            console.error("Error data:", error?.response?.data || error);
            const errorMessage = error.response?.data?.message || error.message || 'An error occurred.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '45px',
            borderRadius: '12px',
            borderColor: state.isFocused ? '#FFC107' : '#e5e7eb',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(255, 193, 7, 0.2)' : 'none',
            '&:hover': {
                borderColor: '#FFC107',
            }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? '#FFC107'
                : state.isFocused
                    ? 'rgba(255, 193, 7, 0.1)'
                    : null,
            color: state.isSelected ? 'white' : 'inherit',
            padding: '10px 12px',
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        })
    };

    const inputClass = `
    w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 text-sm
    focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent
    placeholder:text-gray-400 transition-all shadow-sm
    ${errors ? 'invalid:border-red-300 invalid:ring-red-100' : ''}
  `;

    return (
        <Layout>
            <div className="w-full md:w-[80%] lg:w-[60%] mx-auto">
                <div className="mb-6 bg-gradient-to-r w-full from-yellow-50 to-yellow-100 rounded-2xl p-6 shadow-sm border border-yellow-200">
                    <motion.h1
                        className="text-xl text-center text-gray-800 font-semibold flex items-center justify-center"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <UserPlus size={24} className="mr-2 text-yellow-500" />
                        Onboard New Customer
                    </motion.h1>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">

                        <div className="mt-4 md:mt-0 flex items-center text-sm">
                            <motion.div
                                className={`flex items-center ${step >= 1 ? 'text-yellow-600' : 'text-gray-400'}`}
                                animate={{ scale: step === 1 ? 1.05 : 1 }}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${step >= 1 ? 'bg-yellow-500 text-white' : 'bg-gray-200'}`}>
                                    1
                                </div>
                                <span className="hidden sm:inline">Personal Info</span>
                            </motion.div>

                            <ChevronRight size={16} className="mx-2 text-gray-300" />

                            <motion.div
                                className={`flex items-center ${step >= 2 ? 'text-yellow-600' : 'text-gray-400'}`}
                                animate={{ scale: step === 2 ? 1.05 : 1 }}
                            >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${step >= 2 ? 'bg-yellow-500 text-white' : 'bg-gray-200'}`}>
                                    2
                                </div>
                                <span className="hidden sm:inline">Additional Details</span>
                            </motion.div>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                        <motion.div
                            className="bg-yellow-500 h-2.5"
                            initial={{ width: step === 1 ? "50%" : "100%" }}
                            animate={{ width: step === 1 ? "50%" : "100%" }}
                            transition={{ duration: 0.5 }}
                        ></motion.div>
                    </div>
                </div>

                <form onSubmit={(e) => e.preventDefault()} className="w-full space-y-6">
                    <AnimatePresence mode="wait">
                        {step === 1 && (
                            <motion.div
                                key="step1"
                                variants={staggerContainer}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <FormSection title="Personal Information" icon={<User size={16} />}>
                                    <FormRow>
                                        <FormField label="First Name" required error={errors.first_name}>
                                            <div className="relative">
                                                <input
                                                    id="first_name"
                                                    name="first_name"
                                                    type="text"
                                                    value={formData.first_name}
                                                    onChange={handleInputChange}
                                                    placeholder="E.g John"
                                                    className={inputClass}
                                                />
                                            </div>
                                        </FormField>

                                        <FormField label="Last Name" required error={errors.last_name}>
                                            <input
                                                id="last_name"
                                                name="last_name"
                                                type="text"
                                                value={formData.last_name}
                                                onChange={handleInputChange}
                                                placeholder="E.g Doe"
                                                className={inputClass}
                                            />
                                        </FormField>
                                    </FormRow>

                                    <FormRow>
                                        <FormField label="Email Address" required error={errors.email}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Mail size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="E.g johndoe@example.com"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>

                                        <FormField label="Gender" required error={errors.gender}>
                                            <select
                                                id="gender"
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleInputChange}
                                                className={`${inputClass} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_10px_center] bg-no-repeat pr-10`}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </FormField>
                                    </FormRow>

                                    <FormField label="Phone Number" required error={errors.phone_number || errors.countryCode}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-1/3">
                                                {loadingCountries ? (
                                                    <div className="flex items-center justify-center p-2">
                                                        <Loader2 size={20} className="animate-spin text-yellow-500" />
                                                    </div>
                                                ) : countryCodes.length > 0 ? (
                                                    <Select
                                                        options={countryCodes.map((country) => ({
                                                            label: (
                                                                <div className="flex items-center">
                                                                    <img src={country.flag} alt={country.name} className="w-4 h-4 mr-2 rounded-sm object-cover" />
                                                                    <span>{country.code}</span>
                                                                </div>
                                                            ),
                                                            value: country.code,
                                                        }))}
                                                        onChange={(selected) => {
                                                            setCountryCode(selected.value);
                                                            if (errors.countryCode) {
                                                                setErrors({ ...errors, countryCode: null });
                                                            }
                                                        }}
                                                        placeholder="+254"
                                                        styles={customSelectStyles}
                                                    />
                                                ) : (
                                                    <input
                                                        type="text"
                                                        placeholder="Country Code"
                                                        value={countryCode}
                                                        onChange={(e) => {
                                                            setCountryCode(e.target.value);
                                                            if (errors.countryCode) {
                                                                setErrors({ ...errors, countryCode: null });
                                                            }
                                                        }}
                                                        className={inputClass}
                                                    />
                                                )}
                                            </div>
                                            <input
                                                type="text"
                                                id="phone_number"
                                                name="phone_number"
                                                className={`${inputClass} w-2/3`}
                                                value={formData.phone_number}
                                                placeholder="712345678"
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </FormField>


                                    <FormRow>
                                        <FormField label="ID Number" required error={errors.id_number}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <CreditCard size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="id_number"
                                                    name="id_number"
                                                    type="text"
                                                    value={formData.id_number}
                                                    onChange={handleInputChange}
                                                    placeholder="E.g 73882289"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>

                                        <FormField label="Date of Birth" required error={errors.dob}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Calendar size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="dob"
                                                    name="dob"
                                                    type="date"
                                                    value={formData.dob}
                                                    onChange={handleInputChange}
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>
                                    </FormRow>

                                    <FormField label="Photo" error={errors.photo}>
                                        <div className="flex items-center gap-4">
                                            <div
                                                onClick={() => fileInputRef.current?.click()}
                                                className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                                            >
                                                {photoPreview ? (
                                                    <img
                                                        src={photoPreview}
                                                        alt="Customer"
                                                        className="w-full h-full object-cover rounded-xl"
                                                    />
                                                ) : (
                                                    <>
                                                        <Image size={20} className="text-gray-400 mb-2" />
                                                        <span className="text-xs text-gray-500">Upload photo</span>
                                                    </>
                                                )}
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {photoPreview ? (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center">
                                                            <Check size={16} className="text-green-500 mr-1" />
                                                            <span>Photo uploaded</span>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                setPhotoPreview(null);
                                                                setFormData({ ...formData, photo: null });
                                                            }}
                                                            className="text-red-500 text-xs flex items-center hover:underline"
                                                        >
                                                            <X size={12} className="mr-1" />
                                                            Remove photo
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <p>Upload a clear photo of the customer (optional)</p>
                                                )}
                                            </div>
                                        </div>
                                    </FormField>
                                </FormSection>

                                <div className="flex justify-end">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleNextStep}
                                        className="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-xl shadow-md transition-colors"
                                    >
                                        Next Step
                                        <ChevronRight size={18} />
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}

                        {step === 2 && (
                            <motion.div
                                key="step2"
                                variants={staggerContainer}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <FormSection title="Location Information" icon={<MapPin size={16} />}>
                                    <FormField label="Country of Residence" required error={errors.country}>
                                        {loadingCountries ? (
                                            <div className="flex items-center justify-center p-4">
                                                <Loader2 size={20} className="animate-spin text-yellow-500" />
                                            </div>
                                        ) : countryCodes.length > 0 ? (
                                            <Select
                                                options={countryCodes.map((country) => ({
                                                    label: (
                                                        <div className="flex items-center">
                                                            <img src={country.flag} alt={country.name} className="w-5 h-5 mr-2 rounded-sm object-cover" />
                                                            {country.name}
                                                        </div>
                                                    ),
                                                    value: country.name,
                                                }))}
                                                onChange={(selected) => {
                                                    setFormData({ ...formData, country: selected.value });
                                                    if (errors.country) {
                                                        setErrors({ ...errors, country: null });
                                                    }
                                                }}
                                                placeholder="Select a country"
                                                styles={customSelectStyles}
                                            />
                                        ) : (
                                            <div>
                                                <input
                                                    type="text"
                                                    placeholder="Enter Country Name"
                                                    value={formData.country}
                                                    onChange={(e) => {
                                                        setFormData({ ...formData, country: e.target.value });
                                                        if (errors.country) {
                                                            setErrors({ ...errors, country: null });
                                                        }
                                                    }}
                                                    className={inputClass}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Enter Country Code"
                                                    value={countryCode}
                                                    onChange={(e) => {
                                                        setCountryCode(e.target.value);
                                                        if (errors.countryCode) {
                                                            setErrors({ ...errors, countryCode: null });
                                                        }
                                                    }}
                                                    className={`${inputClass} mt-2`}
                                                />
                                            </div>
                                        )}
                                    </FormField>


                                    <FormField label="Nationality" required error={errors.nationality}>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Flag size={16} className="text-gray-400" />
                                            </div>
                                            <input
                                                id="nationality"
                                                name="nationality"
                                                type="text"
                                                value={formData.nationality}
                                                onChange={handleInputChange}
                                                placeholder="E.g Kenyan"
                                                className={`${inputClass} pl-10`}
                                            />
                                        </div>
                                    </FormField>

                                    <FormRow>
                                        <FormField label="City" required error={errors.city}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Building size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="city"
                                                    name="city"
                                                    type="text"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    placeholder="E.g Nairobi"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>

                                        <FormField label="State/Province" required error={errors.state}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Globe size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="state"
                                                    name="state"
                                                    type="text"
                                                    value={formData.state}
                                                    onChange={handleInputChange}
                                                    placeholder="E.g Shiloh"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>
                                    </FormRow>
                                </FormSection>

                                <FormSection title="Customer Type" icon={<Users size={16} />}>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div
                                            onClick={() => setFormData({ ...formData, customer_type: 'individual' })}
                                            className={`cursor-pointer p-4 rounded-xl border-2 ${formData.customer_type === 'individual'
                                                ? 'border-yellow-500 bg-yellow-50'
                                                : 'border-gray-200 hover:border-yellow-200'
                                                } transition-colors`}
                                        >
                                            <div className="flex items-center mb-2">
                                                <User size={18} className={`mr-2 ${formData.customer_type === 'individual' ? 'text-yellow-500' : 'text-gray-400'}`} />
                                                <span className={`font-medium ${formData.customer_type === 'individual' ? 'text-yellow-700' : 'text-gray-600'}`}>Individual</span>
                                            </div>
                                            <p className="text-xs text-gray-500">Single person account</p>
                                        </div>

                                        <div
                                            onClick={() => setFormData({ ...formData, customer_type: 'group' })}
                                            className={`cursor-pointer p-4 rounded-xl border-2 ${formData.customer_type === 'group'
                                                ? 'border-yellow-500 bg-yellow-50'
                                                : 'border-gray-200 hover:border-yellow-200'
                                                } transition-colors`}
                                        >
                                            <div className="flex items-center mb-2">
                                                <Users size={18} className={`mr-2 ${formData.customer_type === 'group' ? 'text-yellow-500' : 'text-gray-400'}`} />
                                                <span className={`font-medium ${formData.customer_type === 'group' ? 'text-yellow-700' : 'text-gray-600'}`}>Group</span>
                                            </div>
                                            <p className="text-xs text-gray-500">Multiple people group</p>
                                        </div>

                                        <div
                                            onClick={() => setFormData({ ...formData, customer_type: 'joint' })}
                                            className={`cursor-pointer p-4 rounded-xl border-2 ${formData.customer_type === 'joint'
                                                ? 'border-yellow-500 bg-yellow-50'
                                                : 'border-gray-200 hover:border-yellow-200'
                                                } transition-colors`}
                                        >
                                            <div className="flex items-center mb-2">
                                                <UserCheck size={18} className={`mr-2 ${formData.customer_type === 'joint' ? 'text-yellow-500' : 'text-gray-400'}`} />
                                                <span className={`font-medium ${formData.customer_type === 'joint' ? 'text-yellow-700' : 'text-gray-600'}`}>Joint</span>
                                            </div>
                                            <p className="text-xs text-gray-500">Joint account</p>
                                        </div>
                                    </div>

                                    {(formData.customer_type === 'group' || formData.customer_type === 'joint') && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-4"
                                        >
                                            <div className="mb-4">
                                                <div className="flex items-center justify-between">
                                                    <FormField
                                                        label={`${formData.customer_type === 'group' ? 'Group Participants' : 'Joint Account Members'}`}
                                                        required
                                                        error={errors.participants}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData({
                                                                ...formData,
                                                                participants: [
                                                                    ...formData.participants,
                                                                    { first_name: '', last_name: '', email: '', phone_number: '', id_number: '' }
                                                                ]
                                                            });
                                                        }}
                                                        className="text-sm text-yellow-600 flex items-center hover:text-yellow-700"
                                                    >
                                                        <UserPlus size={16} className="mr-1" /> Add Participant
                                                    </button>
                                                </div>

                                                {formData.participants.length === 0 && (
                                                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl my-3">
                                                        <Users size={24} className="mx-auto text-gray-400 mb-2" />
                                                        <p className="text-gray-500 text-sm">No participants added yet</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setFormData({
                                                                    ...formData,
                                                                    participants: [
                                                                        { first_name: '', last_name: '', email: '', phone_number: '', id_number: '' }
                                                                    ]
                                                                });
                                                            }}
                                                            className="mt-3 px-4 py-2 bg-yellow-50 text-yellow-600 rounded-lg text-sm hover:bg-yellow-100 transition-colors"
                                                        >
                                                            Add your first participant
                                                        </button>
                                                    </div>
                                                )}

                                                {formData.participants.map((participant, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="border border-gray-200 rounded-xl p-4 mb-4 bg-white relative"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updatedParticipants = [...formData.participants];
                                                                updatedParticipants.splice(index, 1);
                                                                setFormData({
                                                                    ...formData,
                                                                    participants: updatedParticipants
                                                                });
                                                            }}
                                                            className="absolute top-3 right-3 p-1.5 bg-red-50 text-red-500 rounded-full hover:bg-red-100 transition-colors"
                                                        >
                                                            <X size={14} />
                                                        </button>

                                                        <h5 className="font-medium text-gray-700 mb-3 flex items-center">
                                                            <User size={16} className="mr-2 text-gray-500" />
                                                            Participant {index + 1}
                                                        </h5>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <input
                                                                type="text"
                                                                placeholder="First Name *"
                                                                value={participant.first_name}
                                                                onChange={(e) => {
                                                                    const updatedParticipants = [...formData.participants];
                                                                    updatedParticipants[index] = {
                                                                        ...updatedParticipants[index],
                                                                        first_name: e.target.value
                                                                    };
                                                                    setFormData({
                                                                        ...formData,
                                                                        participants: updatedParticipants
                                                                    });
                                                                }}
                                                                className={inputClass}
                                                            />

                                                            <input
                                                                type="text"
                                                                placeholder="Last Name *"
                                                                value={participant.last_name}
                                                                onChange={(e) => {
                                                                    const updatedParticipants = [...formData.participants];
                                                                    updatedParticipants[index] = {
                                                                        ...updatedParticipants[index],
                                                                        last_name: e.target.value
                                                                    };
                                                                    setFormData({
                                                                        ...formData,
                                                                        participants: updatedParticipants
                                                                    });
                                                                }}
                                                                className={inputClass}
                                                            />
                                                        </div>

                                                        <div className="mt-3">
                                                            <div className="relative">
                                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                    <Mail size={16} className="text-gray-400" />
                                                                </div>
                                                                <input
                                                                    type="email"
                                                                    placeholder="Email Address *"
                                                                    value={participant.email}
                                                                    onChange={(e) => {
                                                                        const updatedParticipants = [...formData.participants];
                                                                        updatedParticipants[index] = {
                                                                            ...updatedParticipants[index],
                                                                            email: e.target.value
                                                                        };
                                                                        setFormData({
                                                                            ...formData,
                                                                            participants: updatedParticipants
                                                                        });
                                                                    }}
                                                                    className={`${inputClass} pl-10`}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                                            <div className="relative">
                                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                    <Phone size={16} className="text-gray-400" />
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Phone Number *"
                                                                    value={participant.phone_number}
                                                                    onChange={(e) => {
                                                                        const updatedParticipants = [...formData.participants];
                                                                        updatedParticipants[index] = {
                                                                            ...updatedParticipants[index],
                                                                            phone_number: e.target.value
                                                                        };
                                                                        setFormData({
                                                                            ...formData,
                                                                            participants: updatedParticipants
                                                                        });
                                                                    }}
                                                                    className={`${inputClass} pl-10`}
                                                                />
                                                            </div>

                                                            <div className="relative">
                                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                    <CreditCard size={16} className="text-gray-400" />
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    placeholder="ID Number *"
                                                                    value={participant.id_number}
                                                                    onChange={(e) => {
                                                                        const updatedParticipants = [...formData.participants];
                                                                        updatedParticipants[index] = {
                                                                            ...updatedParticipants[index],
                                                                            id_number: e.target.value
                                                                        };
                                                                        setFormData({
                                                                            ...formData,
                                                                            participants: updatedParticipants
                                                                        });
                                                                    }}
                                                                    className={`${inputClass} pl-10`}
                                                                />
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </FormSection>

                                <FormSection title="Next of Kin Information" icon={<Heart size={16} />}>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Primary Next of Kin</h4>
                                    <FormRow>
                                        <FormField label="Full Name" required error={errors.next_of_kin1_name}>
                                            <input
                                                id="next_of_kin1_name"
                                                name="next_of_kin1_name"
                                                type="text"
                                                value={formData.next_of_kin1_name}
                                                onChange={handleInputChange}
                                                placeholder="Full name"
                                                className={inputClass}
                                            />
                                        </FormField>

                                        <FormField label="Phone Number" required error={errors.next_of_kin1_phone}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Phone size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="next_of_kin1_phone"
                                                    name="next_of_kin1_phone"
                                                    type="text"
                                                    value={formData.next_of_kin1_phone}
                                                    onChange={handleInputChange}
                                                    placeholder="Phone number"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>
                                    </FormRow>

                                    <FormRow>
                                        <FormField label="Email" error={errors.next_of_kin1_email}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Mail size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="next_of_kin1_email"
                                                    name="next_of_kin1_email"
                                                    type="email"
                                                    value={formData.next_of_kin1_email}
                                                    onChange={handleInputChange}
                                                    placeholder="Email address"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>

                                        <FormField label="ID Number" error={errors.next_of_kin1_idNo}>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <CreditCard size={16} className="text-gray-400" />
                                                </div>
                                                <input
                                                    id="next_of_kin1_idNo"
                                                    name="next_of_kin1_idNo"
                                                    type="text"
                                                    value={formData.next_of_kin1_idNo}
                                                    onChange={handleInputChange}
                                                    placeholder="ID number"
                                                    className={`${inputClass} pl-10`}
                                                />
                                            </div>
                                        </FormField>
                                    </FormRow>

                                    <FormField label="Relationship" required error={errors.next_of_kin1_relationship}>
                                        <select
                                            id="next_of_kin1_relationship"
                                            name="next_of_kin1_relationship"
                                            value={formData.next_of_kin1_relationship}
                                            onChange={handleInputChange}
                                            className={`${inputClass} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_10px_center] bg-no-repeat pr-10`}
                                        >
                                            <option value="">Select Relationship</option>
                                            <option value="Spouse">Spouse</option>
                                            <option value="Parent">Parent</option>
                                            <option value="Child">Child</option>
                                            <option value="Sibling">Sibling</option>
                                            <option value="Relative">Relative</option>
                                            <option value="Friend">Friend</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </FormField>

                                    <div className="border-t border-gray-100 my-6 pt-6">
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">Secondary Next of Kin (Optional)</h4>
                                        <FormRow>
                                            <FormField label="Full Name" error={errors.next_of_kin2_name}>
                                                <input
                                                    id="next_of_kin2_name"
                                                    name="next_of_kin2_name"
                                                    type="text"
                                                    value={formData.next_of_kin2_name}
                                                    onChange={handleInputChange}
                                                    placeholder="Full name"
                                                    className={inputClass}
                                                />
                                            </FormField>

                                            <FormField label="Phone Number" error={errors.next_of_kin2_phone}>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Phone size={16} className="text-gray-400" />
                                                    </div>
                                                    <input
                                                        id="next_of_kin2_phone"
                                                        name="next_of_kin2_phone"
                                                        type="text"
                                                        value={formData.next_of_kin2_phone}
                                                        onChange={handleInputChange}
                                                        placeholder="Phone number"
                                                        className={`${inputClass} pl-10`}
                                                    />
                                                </div>
                                            </FormField>
                                        </FormRow>

                                        <FormRow>
                                            <FormField label="Email" error={errors.next_of_kin2_email}>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <Mail size={16} className="text-gray-400" />
                                                    </div>
                                                    <input
                                                        id="next_of_kin2_email"
                                                        name="next_of_kin2_email"
                                                        type="email"
                                                        value={formData.next_of_kin2_email}
                                                        onChange={handleInputChange}
                                                        placeholder="Email address"
                                                        className={`${inputClass} pl-10`}
                                                    />
                                                </div>
                                            </FormField>

                                            <FormField label="ID Number" error={errors.next_of_kin2_idNo}>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <CreditCard size={16} className="text-gray-400" />
                                                    </div>
                                                    <input
                                                        id="next_of_kin2_idNo"
                                                        name="next_of_kin2_idNo"
                                                        type="text"
                                                        value={formData.next_of_kin2_idNo}
                                                        onChange={handleInputChange}
                                                        placeholder="ID number"
                                                        className={`${inputClass} pl-10`}
                                                    />
                                                </div>
                                            </FormField>
                                        </FormRow>

                                        <FormField label="Relationship" error={errors.next_of_kin2_relationship}>
                                            <select
                                                id="next_of_kin2_relationship"
                                                name="next_of_kin2_relationship"
                                                value={formData.next_of_kin2_relationship}
                                                onChange={handleInputChange}
                                                className={`${inputClass} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236b7280%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_10px_center] bg-no-repeat pr-10`}
                                            >
                                                <option value="">Select Relationship</option>
                                                <option value="Spouse">Spouse</option>
                                                <option value="Parent">Parent</option>
                                                <option value="Child">Child</option>
                                                <option value="Sibling">Sibling</option>
                                                <option value="Relative">Relative</option>
                                                <option value="Friend">Friend</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </FormField>
                                    </div>
                                </FormSection>

                                <div className="flex justify-between">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handlePrevStep}
                                        className="flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                                    >
                                        <ChevronRight size={18} className="rotate-180" />
                                        Back
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className={`
                                          flex items-center gap-2 px-6 py-3 
                                          ${loading ? 'bg-yellow-400' : 'bg-yellow-500 hover:bg-yellow-600'} 
                                          text-white font-medium rounded-xl shadow-md transition-colors
                                        `}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                Create Customer
                                                <Check size={18} />
                                            </>
                                        )}
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </form>
            </div>
        </Layout>
    );
};

export default AddNewCustomer;